import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, TargetIcon, ZapIcon, BrainIcon, CheckCircleIcon, ArrowRightIcon, SparklesIcon, BarChart3Icon } from 'lucide-react';
export function PredictivePage() {
  return <div className="w-full bg-white" data-id="element-7811">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-teal-800 to-cyan-900 text-white py-24 overflow-hidden" data-id="element-7812">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" data-id="element-7813" />
        <div className="absolute inset-0" data-id="element-7814">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" data-id="element-7815" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-7816" />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative" data-id="element-7817">
          <div className="max-w-4xl mx-auto text-center" data-id="element-7818">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full border border-emerald-400/30 mb-6" data-id="element-7819">
              <BrainIcon className="w-4 h-4" data-id="element-7820" />
              <span className="text-sm font-medium" data-id="element-7821">
                AI-Powered Intelligence
              </span>
            </div>
            <h1 className="text-6xl font-bold mb-6 leading-tight" data-id="element-7822">
              Predictive Analytics
            </h1>
            <p className="text-xl text-emerald-100 mb-8 leading-relaxed" data-id="element-7823">
              Know which accounts will buy before they do. AI models score every
              account in real-time so you focus on the right opportunities.
            </p>
            <div className="flex items-center justify-center gap-4" data-id="element-7824">
              <Link to="/signup" className="px-8 py-4 bg-white text-emerald-900 rounded-lg hover:bg-emerald-50 font-medium text-lg shadow-lg hover:shadow-xl transition-all" data-id="element-7825">
                Get Started
              </Link>
              <Link to="/website/contact" className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white/10 font-medium text-lg" data-id="element-7826">
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* What Predictive Does */}
      <section className="py-24 bg-white" data-id="element-7827">
        <div className="max-w-7xl mx-auto px-6" data-id="element-7828">
          <div className="text-center mb-16" data-id="element-7829">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-7830">
              What Predictive does
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-7831">
              AI models analyze 100+ signals to predict which accounts are most
              likely to buy, when they'll buy, and what they'll spend.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-7832">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-8 border-2 border-emerald-200" data-id="element-7833">
              <div className="w-16 h-16 bg-emerald-600 rounded-xl flex items-center justify-center mb-6" data-id="element-7834">
                <TargetIcon className="w-8 h-8 text-white" data-id="element-7835" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-7836">
                Propensity scoring
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-7837">
                Every account gets a 0-100 score showing likelihood to buy.
                Updated in real-time as signals change.
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-8 border-2 border-teal-200" data-id="element-7838">
              <div className="w-16 h-16 bg-teal-600 rounded-xl flex items-center justify-center mb-6" data-id="element-7839">
                <TrendingUpIcon className="w-8 h-8 text-white" data-id="element-7840" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-7841">
                Deal size prediction
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-7842">
                Forecast expected contract value based on company size, tech
                stack, and historical patterns.
              </p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-8 border-2 border-cyan-200" data-id="element-7843">
              <div className="w-16 h-16 bg-cyan-600 rounded-xl flex items-center justify-center mb-6" data-id="element-7844">
                <ZapIcon className="w-8 h-8 text-white" data-id="element-7845" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-7846">
                Timing intelligence
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-7847">
                Predict when accounts will enter buying mode based on budget
                cycles and trigger events.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Key Features */}
      <section className="py-24 bg-gray-50" data-id="element-7848">
        <div className="max-w-7xl mx-auto px-6" data-id="element-7849">
          <div className="text-center mb-16" data-id="element-7850">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-7851">
              Predictive features
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-7852">
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all" data-id="element-7853">
              <div className="flex items-center gap-3 mb-4" data-id="element-7854">
                <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center" data-id="element-7855">
                  <BrainIcon className="w-6 h-6 text-white" data-id="element-7856" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-7857">
                  Multi-signal AI models
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-7858">
                Models trained on 100+ signals including intent, firmographics,
                technographics, engagement, and historical patterns.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-7859">
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm" data-id="element-7860">
                  100+ signals
                </span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm" data-id="element-7861">
                  Real-time updates
                </span>
                <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm" data-id="element-7862">
                  Self-learning
                </span>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all" data-id="element-7863">
              <div className="flex items-center gap-3 mb-4" data-id="element-7864">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center" data-id="element-7865">
                  <TargetIcon className="w-6 h-6 text-white" data-id="element-7866" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-7867">
                  Account prioritization
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-7868">
                Automatically rank accounts by propensity, deal size, and
                timing. Focus sales effort where it matters most.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-7869">
                <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm" data-id="element-7870">
                  Auto-ranking
                </span>
                <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm" data-id="element-7871">
                  Multi-factor
                </span>
                <span className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-sm" data-id="element-7872">
                  Dynamic
                </span>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all" data-id="element-7873">
              <div className="flex items-center gap-3 mb-4" data-id="element-7874">
                <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center" data-id="element-7875">
                  <SparklesIcon className="w-6 h-6 text-white" data-id="element-7876" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-7877">
                  Lookalike modeling
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-7878">
                Find new accounts that match your best customers. AI identifies
                patterns in closed-won deals and surfaces similar prospects.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-7879">
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm" data-id="element-7880">
                  Pattern matching
                </span>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm" data-id="element-7881">
                  ICP expansion
                </span>
                <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm" data-id="element-7882">
                  Continuous learning
                </span>
              </div>
            </div>
            <div className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-all" data-id="element-7883">
              <div className="flex items-center gap-3 mb-4" data-id="element-7884">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center" data-id="element-7885">
                  <BarChart3Icon className="w-6 h-6 text-white" data-id="element-7886" />
                </div>
                <h3 className="text-xl font-bold text-gray-900" data-id="element-7887">
                  Churn prediction
                </h3>
              </div>
              <p className="text-gray-700 mb-4" data-id="element-7888">
                Identify at-risk customers before they churn. Models detect
                early warning signs in usage, engagement, and sentiment.
              </p>
              <div className="flex flex-wrap gap-2" data-id="element-7889">
                <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm" data-id="element-7890">
                  Early warning
                </span>
                <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm" data-id="element-7891">
                  Usage analysis
                </span>
                <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm" data-id="element-7892">
                  Intervention triggers
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-24 bg-white" data-id="element-7893">
        <div className="max-w-7xl mx-auto px-6" data-id="element-7894">
          <div className="text-center mb-16" data-id="element-7895">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-7896">
              How Predictive works
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-7897">
            <div className="text-center" data-id="element-7898">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-7899">
                <span className="text-2xl font-bold text-white" data-id="element-7900">1</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7901">
                Collect signals
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7902">
                Aggregate data from ZoomInfo, CRM, product usage, and engagement
              </p>
            </div>
            <div className="text-center" data-id="element-7903">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-7904">
                <span className="text-2xl font-bold text-white" data-id="element-7905">2</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7906">
                Train models
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7907">
                AI learns from your historical data to predict future outcomes
              </p>
            </div>
            <div className="text-center" data-id="element-7908">
              <div className="w-16 h-16 bg-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-7909">
                <span className="text-2xl font-bold text-white" data-id="element-7910">3</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7911">
                Score accounts
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7912">
                Generate real-time propensity scores for every account in your
                TAM
              </p>
            </div>
            <div className="text-center" data-id="element-7913">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-7914">
                <span className="text-2xl font-bold text-white" data-id="element-7915">4</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-7916">
                Take action
              </h3>
              <p className="text-sm text-gray-600" data-id="element-7917">
                Trigger plays, prioritize outreach, and allocate resources
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Use Cases */}
      <section className="py-24 bg-gray-50" data-id="element-7918">
        <div className="max-w-7xl mx-auto px-6" data-id="element-7919">
          <div className="text-center mb-16" data-id="element-7920">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-7921">
              Common predictive use cases
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-7922">
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all" data-id="element-7923">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-7924">
                Sales prioritization
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-7925">
                Route high-propensity accounts to your best reps. Automatically
                adjust territory assignments based on likelihood to close.
              </p>
              <div className="text-sm text-gray-600" data-id="element-7926">
                <span className="font-medium" data-id="element-7927">Result:</span> 35% higher win rate
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all" data-id="element-7928">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-7929">
                Marketing optimization
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-7930">
                Allocate ad spend to accounts most likely to convert. Stop
                wasting budget on low-propensity targets.
              </p>
              <div className="text-sm text-gray-600" data-id="element-7931">
                <span className="font-medium" data-id="element-7932">Result:</span> 40% lower CAC
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all" data-id="element-7933">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-7934">
                Churn prevention
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-7935">
                Identify at-risk customers 90 days before renewal. Trigger
                intervention plays to save the account.
              </p>
              <div className="text-sm text-gray-600" data-id="element-7936">
                <span className="font-medium" data-id="element-7937">Result:</span> 20% churn reduction
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Model Accuracy */}
      <section className="py-24 bg-white" data-id="element-7938">
        <div className="max-w-7xl mx-auto px-6" data-id="element-7939">
          <div className="text-center mb-16" data-id="element-7940">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-7941">
              Model accuracy & performance
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-7942">
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 border-2 border-emerald-200" data-id="element-7943">
              <div className="text-3xl font-bold text-emerald-600 mb-2" data-id="element-7944">
                92%
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7945">
                Prediction accuracy
              </p>
              <p className="text-xs text-gray-600" data-id="element-7946">
                For top 20% scored accounts
              </p>
            </div>
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border-2 border-teal-200" data-id="element-7947">
              <div className="text-3xl font-bold text-teal-600 mb-2" data-id="element-7948">3.5x</div>
              <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7949">
                Conversion lift
              </p>
              <p className="text-xs text-gray-600" data-id="element-7950">
                High vs low propensity accounts
              </p>
            </div>
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-xl p-6 border-2 border-cyan-200" data-id="element-7951">
              <div className="text-3xl font-bold text-cyan-600 mb-2" data-id="element-7952">85%</div>
              <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7953">
                Churn prediction
              </p>
              <p className="text-xs text-gray-600" data-id="element-7954">90 days before renewal</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200" data-id="element-7955">
              <div className="text-3xl font-bold text-purple-600 mb-2" data-id="element-7956">
                Real-time
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-7957">
                Score updates
              </p>
              <p className="text-xs text-gray-600" data-id="element-7958">As new signals arrive</p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 to-teal-700 text-white" data-id="element-7959">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-7960">
          <h2 className="text-4xl font-bold mb-6" data-id="element-7961">
            Ready to predict your next customers?
          </h2>
          <p className="text-xl text-emerald-100 mb-8" data-id="element-7962">
            Start using AI to prioritize accounts and accelerate revenue
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-7963">
            <Link to="/signup" className="px-8 py-4 bg-white text-emerald-600 rounded-lg hover:bg-emerald-50 font-medium text-lg shadow-lg" data-id="element-7964">
              Get Started
            </Link>
            <Link to="/website/contact" className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white/10 font-medium text-lg" data-id="element-7965">
              Talk to Sales
            </Link>
          </div>
        </div>
      </section>
    </div>;
}